/**
 * Sample Sports IQ questions — UI placeholder only. No quiz engine or scoring.
 * Consumed only by ContestSportsIqTabPanel (`?tab=sports-iq`). Do not import into
 * Benchmark, Learn, foundation catalogs, contest scoring, or self-evaluation.
 */

export type SportsIqQuestionType = 'Tactics' | 'Decision' | 'Scenario' | 'Positioning';
export type SportsIqDifficulty = 'Easy' | 'Medium' | 'Hard';

export interface SportsIqQuestion {
  id: string;
  prompt: string;
  type: SportsIqQuestionType;
  difficulty: SportsIqDifficulty;
  options: string[];
}

const BADMINTON: SportsIqQuestion[] = [
  {
    id: 'bd-1',
    prompt: 'Your opponent is deep in the backcourt after a weak lift. Which reply is most likely to win the rally without giving them a smash?',
    type: 'Scenario',
    difficulty: 'Easy',
    options: [
      'A tight net spinner to the closest sideline',
      'A high, slow clear to the same corner',
      'A body smash from mid-court',
      'A drive into their hip',
    ],
  },
  {
    id: 'bd-2',
    prompt: 'When should you choose a net kill instead of a tight spinning net shot?',
    type: 'Decision',
    difficulty: 'Medium',
    options: [
      'When the shuttle is above net tape and the opponent is late to the T',
      'Whenever you are in the forecourt, regardless of shuttle height',
      'Only on the opponent’s first serve return',
      'When you need to slow the rally and recover',
    ],
  },
  {
    id: 'bd-3',
    prompt: 'Serving to a tall attacker standing tight at the T — which serve best reduces their smash angle?',
    type: 'Tactics',
    difficulty: 'Medium',
    options: [
      'A low serve tight to the T with little float',
      'A high flick serve to the back tramlines every time',
      'A drive serve at chest height',
      'A high serve to the mid-court',
    ],
  },
  {
    id: 'bd-4',
    prompt: 'Opponent’s racket is late on a smash. Where should you recover first?',
    type: 'Positioning',
    difficulty: 'Hard',
    options: [
      'Split-step toward the likely block into the open court',
      'Stay planted in the rear tramlines',
      'Sprint to the opposite sideline before they contact',
      'Retreat to the baseline to wait for a clear',
    ],
  },
  {
    id: 'bd-5',
    prompt: 'Which two-shot combination is most likely to open the tramlines after a body smash is blocked?',
    type: 'Tactics',
    difficulty: 'Hard',
    options: [
      'Follow the smash with a fast flat drive to the opposite sideline',
      'Lift high to the same corner and wait',
      'Drop immediately to the same side they blocked',
      'Cross-court clear then another smash to the body',
    ],
  },
];

const FOOTBALL: SportsIqQuestion[] = [
  {
    id: 'fb-1',
    prompt: 'Ball on the wing, overlapping full-back arriving, striker isolated in the box. What is the highest-percentage next action?',
    type: 'Scenario',
    difficulty: 'Easy',
    options: [
      'Cut-back to the penalty spot rather than a high far-post cross',
      'Shoot from the touchline',
      'Switch play all the way back to the keeper',
      'Hold the ball and wait for a second striker who is 40m away',
    ],
  },
  {
    id: 'fb-2',
    prompt: 'You lose the ball in the opponent’s half. When is a high press the right decision versus dropping into a mid-block?',
    type: 'Decision',
    difficulty: 'Hard',
    options: [
      'Press if nearby teammates can trap the first receiver; drop if the first pass is already free',
      'Always press for 10 seconds after every loss',
      'Always drop so you never get countered',
      'Press only when you are winning',
    ],
  },
  {
    id: 'fb-3',
    prompt: 'Keeper is covering the far post on a cut-in. Do you shoot near post or cut back?',
    type: 'Tactics',
    difficulty: 'Medium',
    options: [
      'Cut back if a runner occupies the six-yard line; near-post shot if that runner is marked',
      'Always shoot far post',
      'Always chip the keeper',
      'Turn back and recycle to centre-back',
    ],
  },
  {
    id: 'fb-4',
    prompt: 'Opponent is playing a high defensive line. Which run type is most likely to create a chance?',
    type: 'Tactics',
    difficulty: 'Easy',
    options: [
      'Timed run in behind, starting as the ball is played',
      'Checking short into the striker’s feet every time',
      'Standing offside until the whistle',
      'Dropping into your own box',
    ],
  },
  {
    id: 'fb-5',
    prompt: '2v1 on the counter. When do you dribble versus squaring the ball?',
    type: 'Decision',
    difficulty: 'Medium',
    options: [
      'Commit the last defender, then square if they step; dribble if they freeze',
      'Always shoot immediately',
      'Always square, even if the teammate is covered',
      'Stop and wait for a third attacker',
    ],
  },
];

const CRICKET: SportsIqQuestion[] = [
  {
    id: 'ck-1',
    prompt: 'New ball, two slips, batter leaving well outside off. What is the next bowling plan?',
    type: 'Tactics',
    difficulty: 'Medium',
    options: [
      'Bring the line closer to off stump and hold a catching cover',
      'Bowl short every ball at the body',
      'Switch to spin immediately',
      'Place everyone on the boundary',
    ],
  },
  {
    id: 'ck-2',
    prompt: 'Last over, 8 needed, deep square vacant. Which match-up is most likely to work?',
    type: 'Scenario',
    difficulty: 'Hard',
    options: [
      'Full, straight, at the stumps — force a hit to the packed off side',
      'Short ball every delivery with no fine leg',
      'Wide yorker down leg with no fine leg',
      'Full toss to invite a catch at long-on',
    ],
  },
  {
    id: 'ck-3',
    prompt: 'Mistimed drive stops a few metres from cover. When do you take the single?',
    type: 'Decision',
    difficulty: 'Easy',
    options: [
      'Only if the fielder is turning and your partner has already committed',
      'Always run — hesitation loses wickets less often than dots',
      'Never run on a mistimed shot',
      'Wait for the umpire to call',
    ],
  },
  {
    id: 'ck-4',
    prompt: 'Spinner into the rough, batter sweeping. Where do you post the key fielder?',
    type: 'Positioning',
    difficulty: 'Medium',
    options: [
      'Leg-side catching / short fine for the top edge, with deep square for the miss-hit',
      'Both extra covers, leaving the sweep vacant',
      'Long-off only',
      'Slip and gully as if it were a new-ball seamer',
    ],
  },
  {
    id: 'ck-5',
    prompt: 'Powerplay over 6, extra cover open, batter backing away. What length do you bowl?',
    type: 'Decision',
    difficulty: 'Hard',
    options: [
      'Full at the stumps / yorker so backing away exposes the wicket',
      'Bouncer every ball with third man up',
      'Half-volley wide of off so they can free the arms',
      'Slow full toss at hip height',
    ],
  },
];

const BY_SPORT: Record<string, SportsIqQuestion[]> = {
  Badminton: BADMINTON,
  Football: FOOTBALL,
  Cricket: CRICKET,
};

export function getSportsIqQuestions(sportName: string): SportsIqQuestion[] {
  return BY_SPORT[sportName] ?? BADMINTON;
}
